import { useEffect, useState } from "react";

import { RpcError } from "@buildpy/api/client";
import { LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";
import { clearOnboardingDrafts } from "@/modules/onboarding/draft-storage";

export const RESET_DONE_TEXT = "비밀번호를 바꿨습니다. 새 비밀번호로 로그인하세요.";
export const RESET_LOCAL_ONLY_TEXT = "다른 기기의 로그인은 끊지 못했습니다. 로그인한 뒤 다른 기기에서도 로그아웃하세요.";
export const RESET_FAILED_TEXT = "비밀번호를 바꾸지 못했습니다. 잠시 후 다시 시도하세요.";

// Supabase 오류 코드 → 문구 · 세션이 죽었으면 null(화면이 만료 안내로 바뀐다).
export const resetFailureOf = (code: string | undefined): string | null => {
  if (code === "session_not_found" || code === "session_expired" || code === "refresh_token_not_found" || code === "bad_jwt") return null;
  if (code === "same_password") return "지금 비밀번호와 다른 비밀번호를 쓰세요.";
  if (code === "weak_password") return "더 길거나 복잡한 비밀번호를 쓰세요.";
  return RESET_FAILED_TEXT;
};

export interface ResetSaveDeps {
  status: () => Promise<{ needsPassword: boolean } | null>;
  setSignupPassword: (input: { password: string; confirm: string }) => Promise<{ ok: boolean; message: string }>;
  updateUser: (password: string) => Promise<{ error: { code?: string } | null }>;
}

export type ResetOutcome = { kind: "done" } | { kind: "expired" } | { kind: "failed"; message: string };

// 서버가 세션을 거절했다(401 토큰 없음·만료 · 403 메일 확인 전) — 폼 대신 만료 안내로.
const sessionRejected = (error: unknown): boolean => error instanceof RpcError && (error.status === 401 || error.status === 403);

const failedOrExpired = (error: unknown): ResetOutcome => (sessionRejected(error) ? { kind: "expired" } : { kind: "failed", message: RESET_FAILED_TEXT });

// 비밀번호를 아직 안 정한 셀프 가입 계정이면 서버(service_role)가 비밀번호와 password_set 을 같이 쓴다 · 상태를 못 읽으면 비밀번호를 바꾸지 않고 멈춘다(password_set 이 빠지지 않게) · 그 밖은 Supabase updateUser.
export const saveResetPassword = async (password: string, confirm: string, deps: ResetSaveDeps): Promise<ResetOutcome> => {
  let status: { needsPassword: boolean } | null;
  try {
    status = await deps.status();
  } catch (error) {
    return failedOrExpired(error);
  }
  if (!status) return { kind: "failed", message: RESET_FAILED_TEXT };
  if (status.needsPassword) {
    try {
      const result = await deps.setSignupPassword({ password, confirm });
      return result.ok ? { kind: "done" } : { kind: "failed", message: result.message };
    } catch (error) {
      return failedOrExpired(error);
    }
  }
  const { error } = await deps.updateUser(password);
  if (!error) return { kind: "done" };
  const message = resetFailureOf(error.code);
  return message === null ? { kind: "expired" } : { kind: "failed", message };
};

type SignOutAuth = { signOut: (options: { scope: "global" | "local" }) => Promise<{ error: unknown }> };

// 바꾼 뒤 모든 기기 세션을 끊고(실패하면 이 브라우저만이라도) 이 탭의 시공사 만들기 초안도 지운 뒤(onSession clear 와 같다) 로그인 화면으로 — done 에 모든 기기를 끊었는지 알린다.
export const finishReset = async (auth: SignOutAuth, done: (everywhere: boolean) => void): Promise<void> => {
  const signedOut = await auth.signOut({ scope: "global" }).then((result) => !result.error, () => false);
  if (!signedOut) await auth.signOut({ scope: "local" }).catch(() => undefined);
  clearOnboardingDrafts();
  done(signedOut);
};

// 로그인 화면이 띄울 바꿈 안내 — done(모든 기기 끊음) · local(이 브라우저만).
export type ResetNotice = "done" | "local";

// 주소창 ?reset= 값 → 안내 종류 · 모르는 값은 버린다.
export const resetNoticeOf = (value: unknown): ResetNotice | undefined => (value === "done" || value === "local" ? value : undefined);

// 바꾼 뒤 갈 로그인 주소 — 안내는 토스트(몇 초 뒤 사라진다) 대신 ?reset= 로 넘겨 로그인 화면에 남긴다.
export const loginAfterResetHref = (everywhere: boolean): string => `${LOGIN_PATH}?reset=${everywhere ? "done" : "local"}`;

// 로그인 주소에서 ?reset= 을 지운다 — 같은 /login 으로 replace(뒤로가기 기록에도 안 남는다).
export const clearResetSearch = (router: { navigate: (options: { href: string; replace: boolean }) => unknown }): void => {
  void router.navigate({ href: LOGIN_PATH, replace: true });
};

// ?reset= 안내는 화면 상태로 옮겨 들고 주소에선 clear 로 지운다 — 새로고침·뒤로가기·로그인 실패 뒤엔 다시 안 뜨고 이 화면에 있는 동안만 보인다.
export const useHeldResetNotice = (searched: ResetNotice | undefined, clear: () => void): ResetNotice | undefined => {
  const [held, setHeld] = useState(searched);
  useEffect(() => {
    if (!searched) return;
    setHeld(searched);
    clear();
  }, [searched, clear]);
  return held;
};
